import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaPlane,
  FaTimes,
  FaMapMarkerAlt,
  FaUsers,
  FaCalendarAlt,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaDownload,
  FaEnvelope,
  FaFilePdf,
  FaImage,
} from "react-icons/fa";
import AdminDocumentUploader from "../AdminDocumentUploader";
import AdminDocumentViewer from "../AdminDocumentViewer";
import { formatPriceSync } from "../../utils/currency";
import { useCurrency } from "../../context/CurrencyContext";
import {
  getFlightsFromResponse,
  getFareFromResponse,
  getRouteFromSegments,
  formatDateTime,
} from "../../utils/flightBookingUtils";

const BookingDetailModal = ({ viewItem, onClose }) => {
  const { currency } = useCurrency();
  const [uploading, setUploading] = useState(false);
  const [localItem, setLocalItem] = useState(viewItem);

  // Fallback to viewItem if localItem isn't set
  const currentItem = localItem || viewItem;
  if (!currentItem) return null;

  const isRoundTripGroup = currentItem.type === "roundtrip";
  const onwardB = isRoundTripGroup ? currentItem.onward : currentItem.booking;
  const returnB = isRoundTripGroup ? currentItem.return : null;
  const booking = onwardB;

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const bookingId = booking._id;
    const formData = new FormData();
    formData.append("eTicket", file);
    setUploading(true);
    try {
      const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "";
      const res = await axios.put(`${API_BASE}/api/booking/admin/bookings/${bookingId}/e-ticket`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      if (res.data.success) {
        toast.success("E-ticket uploaded successfully");
        // Update localItem to reflect the change
        const updatedBooking = { ...booking, eTicketPdfPath: res.data.data.eTicketPdfPath };
        const updatedItem = isRoundTripGroup
          ? { ...currentItem, onward: updatedBooking }
          : { ...currentItem, booking: updatedBooking };
        setLocalItem(updatedItem);
      } else {
        toast.error(res.data.error || "Failed to upload e-ticket");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const resOn = onwardB?.response || {};
  const resRet = returnB?.response || {};
  let onwardFlights = getFlightsFromResponse(resOn, "onward");
  let returnFlights = isRoundTripGroup 
    ? getFlightsFromResponse(resRet, "onward") 
    : getFlightsFromResponse(resOn, "return");
  let routeOn = getRouteFromSegments(onwardFlights);
  let routeRet = getRouteFromSegments(returnFlights);

  if (onwardB?.isCustomFlight && onwardB?.customFlightDetails) {
    routeOn = { dep: onwardB.customFlightDetails.from, arr: onwardB.customFlightDetails.to };
    if (onwardB.customFlightDetails.returnDate) {
      routeRet = { dep: onwardB.customFlightDetails.to, arr: onwardB.customFlightDetails.from };
    }
    const customSegments = onwardB.customFlightDetails.segments || [];
    const mapCustomSegment = (s) => {
      let depRaw = String(s.departureTime || "").replace(/\D/g, "");
      let arrRaw = String(s.arrivalTime || "").replace(/\D/g, "");
      return {
        depCName: s.from || onwardB.customFlightDetails.from || "",
        depCode: "",
        arrCName: s.to || onwardB.customFlightDetails.to || "",
        arrCode: "",
        flightNumber: s.flightNo || "",
        flightNo: s.flightNo || "",
        airlineName: s.airline || "",
        airName: s.airline || "",
        depDate: depRaw,
        arrDate: arrRaw,
        depTime: "",
        arrTime: "",
        duration: s.duration || "",
        layovers: s.layovers || []
      };
    };
    onwardFlights = customSegments.filter(s => s.direction === 'onward').map(mapCustomSegment);
    returnFlights = customSegments.filter(s => s.direction === 'return').map(mapCustomSegment);
    if (onwardFlights.length === 0 && customSegments.length > 0) {
       onwardFlights = customSegments.map(mapCustomSegment);
    }
  }
  const fareOn = getFareFromResponse(resOn);
  const fareRet = returnB ? getFareFromResponse(resRet) : null;
  const fareOnNum = fareOn != null ? (typeof fareOn === "number" ? fareOn : parseFloat(fareOn)) : (onwardB?.amount || 0);
  const fareRetNum = fareRet != null ? (typeof fareRet === "number" ? fareRet : parseFloat(fareRet)) : (returnB?.amount || 0);
  const fare = isRoundTripGroup ? fareOnNum + fareRetNum : fareOnNum;
  const isRoundTrip = isRoundTripGroup || (booking.tripType === "1" || booking.tripType === 1) || returnFlights.length > 0;
  const passengers = booking.passenger || [];
  const totalAdults = passengers.filter((p) => p.pType?.toLowerCase() === "a" || p.pType === "adt").length;
  const totalChildren = passengers.filter((p) => p.pType?.toLowerCase() === "c" || p.pType === "chd").length;
  const totalInfants = passengers.filter((p) => p.pType?.toLowerCase() === "i" || p.pType === "inf").length;

  const pnrOn = resOn?.Ticket?.Onward?.passenger || resOn?.Ticket?.passenger || resOn?.ticket?.Onward?.passenger || resOn?.ticket?.passenger || [];
  const pnrRet = returnB ? (resRet?.Ticket?.Onward?.passenger || resRet?.Ticket?.passenger || resRet?.ticket?.Onward?.passenger || resRet?.ticket?.passenger || []) : [];
  const pnrRaw = [...(Array.isArray(pnrOn) ? pnrOn : pnrOn ? [pnrOn] : []), ...(Array.isArray(pnrRet) ? pnrRet : pnrRet ? [pnrRet] : [])];
  
  if (onwardB?.isCustomFlight && onwardB?.customFlightDetails?.pnr) {
    pnrRaw.push({ pnr: onwardB.customFlightDetails.pnr });
  }
  
  const seenPnr = new Set();
  const pnr = pnrRaw.filter((p) => {
    const k = p?.pnr || JSON.stringify(p);
    if (seenPnr.has(k)) return false;
    seenPnr.add(k);
    return true;
  });

  const statusDisplay = (booking.bookingStatus || "success")?.toLowerCase();
  const statusClass =
    statusDisplay === "confirmed" || statusDisplay === "success"
      ? "bg-green-100 text-green-800"
      : statusDisplay === "pending"
      ? "bg-orange-100 text-orange-800"
      : "bg-red-100 text-red-800";

  const toArray = (val) => Array.isArray(val) ? val : (val ? [val] : []);

  const getOnwardSeats = (res) => {
    let seats = [];
    if (res?.ssrinfo?.Onward?.Seat) seats = [...seats, ...toArray(res.ssrinfo.Onward.Seat)];
    if (res?.Ssrinfo?.Onward?.Seat) seats = [...seats, ...toArray(res.Ssrinfo.Onward.Seat)];
    return seats;
  };

  const getReturnSeats = (res) => {
    let seats = [];
    if (res?.ssrinfo?.Return?.Seat) seats = [...seats, ...toArray(res.ssrinfo.Return.Seat)];
    if (res?.Ssrinfo?.Return?.Seat) seats = [...seats, ...toArray(res.Ssrinfo.Return.Seat)];
    return seats;
  };

  const getOnwardBaggage = (res) => {
    let bagg = [];
    if (res?.ssrinfo?.Onward?.Bagg) bagg = [...bagg, ...toArray(res.ssrinfo.Onward.Bagg)];
    if (res?.Ssrinfo?.Onward?.Bagg) bagg = [...bagg, ...toArray(res.Ssrinfo.Onward.Bagg)];
    return bagg;
  };

  const getReturnBaggage = (res) => {
    let bagg = [];
    if (res?.ssrinfo?.Return?.Bagg) bagg = [...bagg, ...toArray(res.ssrinfo.Return.Bagg)];
    if (res?.Ssrinfo?.Return?.Bagg) bagg = [...bagg, ...toArray(res.Ssrinfo.Return.Bagg)];
    return bagg;
  };

  const getOnwardMeals = (res) => {
    let meals = [];
    if (res?.ssrinfo?.Onward?.Meal) meals = [...meals, ...toArray(res.ssrinfo.Onward.Meal)];
    if (res?.Ssrinfo?.Onward?.Meal) meals = [...meals, ...toArray(res.Ssrinfo.Onward.Meal)];
    return meals;
  };

  const getReturnMeals = (res) => {
    let meals = [];
    if (res?.ssrinfo?.Return?.Meal) meals = [...meals, ...toArray(res.ssrinfo.Return.Meal)];
    if (res?.Ssrinfo?.Return?.Meal) meals = [...meals, ...toArray(res.Ssrinfo.Return.Meal)];
    return meals;
  };

  const onwardSeats = getOnwardSeats(resOn);
  const returnSeats = isRoundTripGroup
    ? [...getOnwardSeats(resRet), ...getReturnSeats(resRet)]
    : getReturnSeats(resOn);

  const onwardBaggage = getOnwardBaggage(resOn);
  const returnBaggage = isRoundTripGroup
    ? [...getOnwardBaggage(resRet), ...getReturnBaggage(resRet)]
    : getReturnBaggage(resOn);

  const onwardMeals = getOnwardMeals(resOn);
  const returnMeals = isRoundTripGroup
    ? [...getOnwardMeals(resRet), ...getReturnMeals(resRet)]
    : getReturnMeals(resOn);

  const amountPaidOn = onwardB?.amount || 0;
  const amountPaidRet = returnB?.amount || 0;
  const totalPaid = amountPaidOn + amountPaidRet;
  
  const ssrTotal = onwardSeats.reduce((s, x) => s + (Number(x.seatAmt) || 0), 0) +
                   returnSeats.reduce((s, x) => s + (Number(x.seatAmt) || 0), 0) +
                   onwardBaggage.reduce((s, x) => s + (Number(x.baggAmt) || 0), 0) +
                   returnBaggage.reduce((s, x) => s + (Number(x.baggAmt) || 0), 0) +
                   onwardMeals.reduce((s, x) => s + (Number(x.mealAmt) || 0), 0) +
                   returnMeals.reduce((s, x) => s + (Number(x.mealAmt) || 0), 0);

  // Ensure the grand total includes the base fare and all SSRs, even if amountPaid is missing or incorrectly saved
  let grandTotal = Math.max(totalPaid, fare + ssrTotal);
  let ssrServicesTotal = grandTotal - fare;
  if (ssrServicesTotal < 0) ssrServicesTotal = 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center gap-3">
            <FaPlane className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-bold">Booking Details</h2>
              <p className="text-sm text-blue-100">
                {isRoundTripGroup && returnB
                  ? `${onwardB.refID || "—"} / ${returnB.refID || "—"}`
                  : (booking.refID || booking.ftdBookingID || booking.bookingID || "—")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Route & Status */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-gray-900">
                {routeOn ? (
                  <>
                    {routeOn.dep} → {routeOn.arr}
                    {routeRet && (
                      <span className="block text-base font-semibold text-indigo-600 mt-1">
                        ⇄ Return: {routeRet.dep} → {routeRet.arr}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-500">Flight #{booking.flightID || "—"}</span>
                )}
              </h3>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${statusClass}`}>
                {(booking.bookingStatus || "success").toUpperCase()}
              </span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {booking.serType == 1 ? "Domestic" : booking.serType == 2 ? "International" : "Flight"}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${booking.includeSSR ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                  {booking.includeSSR ? "With SSR" : "Without SSR"}
                </span>
              </div>
            </div>
          </div>

          {/* Onward Journey */}
          {onwardFlights.length > 0 && (
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <h4 className="text-sm font-bold text-blue-800 mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FaPlane className="text-blue-600" />
                  {isRoundTrip ? "Onward Journey" : "Journey"}
                </div>
                {onwardFlights.length > 1 ? (
                  <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {onwardFlights.length - 1} Stop(s) via {onwardFlights.slice(0, -1).map(f => f.arrCode || f.arrCity || f.arrCName).join(', ')}
                  </span>
                ) : (
                  <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    Non-stop
                  </span>
                )}
              </h4>
              <div className="space-y-3">
                {onwardFlights.map((flight, idx) => {
                  const depDt = formatDateTime(flight.depDate, flight.depTime);
                  const arrDt = formatDateTime(flight.arrDate, flight.arrTime);
                  return (
                    <div key={idx} className="bg-white p-3 rounded-lg border border-blue-100">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className="font-semibold text-gray-900">
                          {flight.depCName || flight.depCity || flight.depCode || "—"} ({flight.depCode || "—"}) → {flight.arrCName || flight.arrCity || flight.arrCode || "—"} ({flight.arrCode || "—"})
                        </span>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {flight.flightNumber || flight.flightNo || "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="text-gray-500 block text-xs">Departure</span>
                          {depDt.date} at {depDt.time}
                        </div>
                        <div>
                          <span className="text-gray-500 block text-xs">Arrival</span>
                          {arrDt.date} at {arrDt.time}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        {flight.airName || flight.airlineName || "—"} • Duration: {flight.duration || flight.flightDuration || flight.Duration || "—"} {typeof flight.duration === 'string' && (flight.duration.includes("mins") || flight.duration.includes("h")) ? "" : "mins"}
                      </div>
                      {/* Layover Box */}
                      {flight.layovers && flight.layovers.length > 0 && (
                        <div className="mt-3 bg-amber-50 rounded-lg p-3 border border-amber-100">
                          <p className="text-xs font-semibold text-amber-800 mb-2">Layover / Connection</p>
                          <div className="space-y-1">
                            {flight.layovers.map((lo, li) => (
                              <div key={`lo-on-${idx}-${li}`} className="text-xs text-amber-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full"></span>
                                {lo.airport} - <span className="font-medium">{lo.duration}</span>
                                {lo.terminal && <span className="text-amber-600">({lo.terminal})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Return Journey - from return leg for grouped round trip */}
          {isRoundTrip && returnFlights.length > 0 && (
            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
              <h4 className="text-sm font-bold text-green-800 mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FaPlane className="text-green-600 rotate-180" />
                  Return Journey
                </div>
                {returnFlights.length > 1 ? (
                  <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-1 rounded">
                    {returnFlights.length - 1} Stop(s) via {returnFlights.slice(0, -1).map(f => f.arrCode || f.arrCity || f.arrCName).join(', ')}
                  </span>
                ) : (
                  <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-1 rounded">
                    Non-stop
                  </span>
                )}
              </h4>
              <div className="space-y-3">
                {returnFlights.map((flight, idx) => {
                  const depDt = formatDateTime(flight.depDate, flight.depTime);
                  const arrDt = formatDateTime(flight.arrDate, flight.arrTime);
                  return (
                    <div key={idx} className="bg-white p-3 rounded-lg border border-green-100">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className="font-semibold text-gray-900">
                          {flight.depCName || flight.depCity || flight.depCode || "—"} ({flight.depCode || "—"}) → {flight.arrCName || flight.arrCity || flight.arrCode || "—"} ({flight.arrCode || "—"})
                        </span>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {flight.flightNumber || flight.flightNo || "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="text-gray-500 block text-xs">Departure</span>
                          {depDt.date} at {depDt.time}
                        </div>
                        <div>
                          <span className="text-gray-500 block text-xs">Arrival</span>
                          {arrDt.date} at {arrDt.time}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        {flight.airName || flight.airlineName || "—"} • Duration: {flight.duration || flight.flightDuration || flight.Duration || "—"} {typeof flight.duration === 'string' && (flight.duration.includes("mins") || flight.duration.includes("h")) ? "" : "mins"}
                      </div>
                      {/* Layover Box */}
                      {flight.layovers && flight.layovers.length > 0 && (
                        <div className="mt-3 bg-amber-50 rounded-lg p-3 border border-amber-100">
                          <p className="text-xs font-semibold text-amber-800 mb-2">Layover / Connection</p>
                          <div className="space-y-1">
                            {flight.layovers.map((lo, li) => (
                              <div key={`lo-ret-${idx}-${li}`} className="text-xs text-amber-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full"></span>
                                {lo.airport} - <span className="font-medium">{lo.duration}</span>
                                {lo.terminal && <span className="text-amber-600">({lo.terminal})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selected Services (SSR) */}
          {(onwardSeats.length > 0 || returnSeats.length > 0 || onwardBaggage.length > 0 || returnBaggage.length > 0 || onwardMeals.length > 0 || returnMeals.length > 0) && (
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
              <h4 className="text-sm font-bold text-purple-800 mb-2 flex items-center gap-2">
                <FaUsers className="text-purple-600" />
                Selected Services (SSR)
              </h4>
              <div className="flex flex-wrap gap-4 mt-3">
                {/* ONWARD SSR */}
                {(onwardSeats.length > 0 || onwardBaggage.length > 0 || onwardMeals.length > 0) && (
                  <div className="flex-1 min-w-[200px]">
                    <span className="text-xs font-semibold text-purple-700 block mb-2 uppercase tracking-wider">Onward</span>
                    <div className="flex gap-2 flex-wrap">
                      {onwardSeats.map((s, i) => (
                        <span key={`seat-${i}`} className="px-2 py-1 bg-white border border-purple-200 text-purple-700 text-xs font-medium rounded-md shadow-sm">
                          Seat: {s.seatName || s.seatID} <span className="text-gray-400 font-normal">|</span> ₹{s.seatAmt || 0}
                        </span>
                      ))}
                      {onwardBaggage.map((b, i) => (
                        <span key={`bagg-${i}`} className="px-2 py-1 bg-white border border-purple-200 text-purple-700 text-xs font-medium rounded-md shadow-sm">
                          Baggage: {b.baggDesc || b.baggID} <span className="text-gray-400 font-normal">|</span> ₹{b.baggAmt || 0}
                        </span>
                      ))}
                      {onwardMeals.map((m, i) => (
                        <span key={`meal-${i}`} className="px-2 py-1 bg-white border border-purple-200 text-purple-700 text-xs font-medium rounded-md shadow-sm">
                          Meal: {m.mealDesc || m.mealID} <span className="text-gray-400 font-normal">|</span> ₹{m.mealAmt || 0}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* RETURN SSR */}
                {(returnSeats.length > 0 || returnBaggage.length > 0 || returnMeals.length > 0) && (
                  <div className="flex-1 min-w-[200px]">
                    <span className="text-xs font-semibold text-purple-700 block mb-2 uppercase tracking-wider">Return</span>
                    <div className="flex gap-2 flex-wrap">
                      {returnSeats.map((s, i) => (
                        <span key={`r-seat-${i}`} className="px-2 py-1 bg-white border border-purple-200 text-purple-700 text-xs font-medium rounded-md shadow-sm">
                          Seat: {s.seatName || s.seatID} <span className="text-gray-400 font-normal">|</span> ₹{s.seatAmt || 0}
                        </span>
                      ))}
                      {returnBaggage.map((b, i) => (
                        <span key={`r-bagg-${i}`} className="px-2 py-1 bg-white border border-purple-200 text-purple-700 text-xs font-medium rounded-md shadow-sm">
                          Baggage: {b.baggDesc || b.baggID} <span className="text-gray-400 font-normal">|</span> ₹{b.baggAmt || 0}
                        </span>
                      ))}
                      {returnMeals.map((m, i) => (
                        <span key={`r-meal-${i}`} className="px-2 py-1 bg-white border border-purple-200 text-purple-700 text-xs font-medium rounded-md shadow-sm">
                          Meal: {m.mealDesc || m.mealID} <span className="text-gray-400 font-normal">|</span> ₹{m.mealAmt || 0}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Passengers & Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                <FaUsers className="text-blue-600" />
                Passengers
              </h4>
              <p className="text-sm text-gray-700">
                {totalAdults} Adult{totalAdults !== 1 ? "s" : ""}
                {totalChildren > 0 && `, ${totalChildren} Child${totalChildren !== 1 ? "ren" : ""}`}
                {totalInfants > 0 && `, ${totalInfants} Infant${totalInfants !== 1 ? "s" : ""}`}
              </p>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                {passengers.map((p, i) => (
                  <li key={i}>
                    {p.title} {p.fName} {p.lName}
                    {p.pType && (
                      <span className="text-gray-400 ml-1">({p.pType})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                <FaMapMarkerAlt className="text-blue-600" />
                Contact
              </h4>
              <p className="text-sm text-gray-700">{booking.email}</p>
              <p className="text-sm text-gray-700">{booking.mobile}</p>
            </div>
          </div>

          {/* PNR & Fare */}
          <div className="flex flex-wrap gap-4 p-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-gray-200">
            {pnr.length > 0 && (
              <div>
                <span className="text-xs text-gray-500 block">PNR</span>
                <span className="font-semibold text-gray-900">
                  {pnr.map((p, i) => (p?.pnr || p) + (i < pnr.length - 1 ? ", " : ""))}
                </span>
              </div>
            )}
            <div>
              <span className="text-xs text-gray-500 block">Flight Fare</span>
              <span className="font-bold text-lg text-blue-600">
                {formatPriceSync(fare, currency)}
              </span>
            </div>
            {ssrServicesTotal > 0 && (
              <div>
                <span className="text-xs text-gray-500 block">SSR & Services</span>
                <span className="font-bold text-lg text-purple-600">
                  {formatPriceSync(ssrServicesTotal, currency)}
                </span>
              </div>
            )}
            <div>
              <span className="text-xs text-gray-500 block">Grand Total</span>
              <span className="font-bold text-lg text-green-600">
                {formatPriceSync(grandTotal, currency)}
              </span>
            </div>
            <div>
              <span className="text-xs text-gray-500 block">Booked on</span>
              <span className="font-medium text-gray-900">
                {isRoundTripGroup && returnB
                  ? new Date(Math.min(
                      new Date(onwardB.createdAt).getTime(),
                      new Date(returnB.createdAt).getTime()
                    )).toLocaleString("en-IN")
                  : (booking.createdAt ? new Date(booking.createdAt).toLocaleString("en-IN") : "—")}
              </span>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <div className="pt-2">
              {booking.eTicketPdfPath && (
                <div className="mb-4 bg-white p-3 rounded-lg border border-gray-200">
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Custom Flight E-Ticket</h5>
                  <a
                    href={`${import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000"}/${booking.eTicketPdfPath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-50 px-3 py-1.5 rounded-md"
                  >
                    View / Download E-Ticket
                  </a>
                </div>
              )}
              <AdminDocumentViewer adminDocuments={booking.adminDocuments} />
              <AdminDocumentUploader
                type="flight"
                bookingId={booking._id}
                onUploadSuccess={(updatedDocs) => {
                  setBooking((prev) => ({ ...prev, adminDocuments: updatedDocs }));
                  if (onUpdate) {
                    onUpdate({ ...booking, adminDocuments: updatedDocs });
                  }
                }}
              />
            </div>
          </div>

          {/* Fallback when no flight segments */}
          {onwardFlights.length === 0 && (
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <p className="text-sm text-amber-800">
                Flight segment details not available. Ref: {booking.refID} • Flight ID: {booking.flightID}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingDetailModal;
